import { useMutation } from '@apollo/client';
import { DELETE_COMMUNITY_GROUP_ACTION, GET_COMMUNITY_GROUP_ACTIONS } from '@communities/content-management/graphql';
import { transformErrors } from '@core/adapters/errors/transformError';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

interface DeleteCommunityGroupActionResponse {
  deleteCommunityGroupAction: boolean;
}

export function useDeleteCommunityGroupAction(communityGroupId?: string) {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [deleteActionMutation, { loading }] = useMutation<DeleteCommunityGroupActionResponse>(DELETE_COMMUNITY_GROUP_ACTION, {
    refetchQueries: communityGroupId ? [{ query: GET_COMMUNITY_GROUP_ACTIONS, variables: { communityGroupId } }] : [],
    awaitRefetchQueries: true,
  });

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const deleteAction = async (communityGroupId: string, actionName: string): Promise<boolean> => {
    try {
      track('DeleteCommunityGroupActionInitiated', {
        communityGroupId,
        actionName,
      });

      await deleteActionMutation({
        variables: {
          communityGroupId,
          actionName,
        },
      });

      track('DeleteCommunityGroupActionSucceeded', {
        communityGroupId,
        actionName,
      });

      return true;
    } catch (error) {
      track('DeleteCommunityGroupActionFailed', {
        communityGroupId,
        actionName,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });

      setErrors(transformErrors(error));

      return false;
    }
  };

  return {
    deleteAction,
    loading,
    errors,
    resetErrors,
  };
}

export default useDeleteCommunityGroupAction;
